import { selectCurrentHotelName } from 'accessors'
import {
  PrimaryRegularButton,
  SecondaryRegularButton
} from 'components/ReusableComponents/Button/RegularButtons'
import CloseIcon from 'components/ReusableComponents/CloseIcon'
import Dialog from 'components/ReusableComponents/Dialog'
import {
  EmailEditorProvider,
  useEmailEditor
} from 'features/EmailEditor/EmailEditorContext'
import EmailEditorPanel from 'features/EmailEditor/EmailEditorPanel'
import EmailPreview from 'features/EmailEditor/EmailPreview'
import TestEmail from 'features/EmailEditor/TestEmail'
import PropTypes from 'prop-types'
import { useState } from 'react'
import { useSelector } from 'react-redux'

import ConfigurationStep from './ConfigurationStep'
import styles from './EditStepDialog.module.scss'
import IncentiveStep from './IncentiveStep'
import LandingContentStep from './LandingContentStep'
import TextMessageStep from './TextMessageStep'

const steps = {
  configuration: 'configuration',
  text: 'text',
  email: 'email',
  landing: 'landing',
  incentives: 'incentives'
}

const stepNames = {
  configuration: 'Configuration',
  text: 'Text',
  email: 'Email',
  landing: 'Landing',
  incentives: 'Incentives'
}

function normalizeLanding(landing) {
  return {
    header: landing?.header ?? landing?.Header ?? '',
    body: landing?.body ?? landing?.Body ?? '',
    cta: landing?.cta ?? landing?.Cta ?? '',
    successTitle: landing?.successTitle ?? landing?.SuccessTitle ?? '',
    successBody: landing?.successBody ?? landing?.SuccessBody ?? '',
    successClosing: landing?.successClosing ?? landing?.SuccessClosing ?? ''
  }
}

function normalizeIncentive(incentive) {
  return {
    promotionCode: incentive?.promotionCode ?? incentive?.PromotionCode ?? '',
    promotionTagline: incentive?.promotionTagline ?? incentive?.PromotionTagline ?? '',
    minNightsToStay: Number(
      incentive?.minNightsToStay ?? incentive?.MinNightsToStay ?? 1
    ),
    maxDiscountPercentage: Number(
      incentive?.maxDiscountPercentage ?? incentive?.MaxDiscountPercentage ?? 0
    )
  }
}

function EditStepDialogInner({
  title,
  onClose,
  onSave,
  isSaving,
  initialData,
  timingKind,
  timingLabel,
  contentId,
  hotelName,
  firstName
}) {
  const hotelNameFromStore = useSelector(selectCurrentHotelName)
  const resolvedHotelName = hotelName || hotelNameFromStore || 'the hotel'

  const [activeStep, setActiveStep] = useState(steps.configuration)
  const [isEnabled, setIsEnabled] = useState(initialData?.isEnabled ?? true)
  const [daysBeforeCheckIn, setDaysBeforeCheckIn] = useState(
    initialData?.daysBeforeCheckIn ?? 3
  )
  const [messageTemplate, setMessageTemplate] = useState(initialData?.messageTemplate || '')
  const [landing, setLanding] = useState(normalizeLanding(initialData?.landing))
  const [incentive, setIncentive] = useState(normalizeIncentive(initialData?.incentive))

  const { refreshEmailData, saveChanges, hasUnsavedChanges } = useEmailEditor()

  const isEditingEmail = activeStep === steps.email && Boolean(contentId)

  const handleSave = async () => {
    if (activeStep === steps.email && hasUnsavedChanges) {
      await saveChanges()
    }

    onSave?.({
      isEnabled,
      daysBeforeCheckIn,
      messageTemplate,
      landing: normalizeLanding(landing),
      incentive: normalizeIncentive(incentive)
    })
  }

  return (
    <Dialog verticalPadding={30}>
      <div className={styles.resolutionWrapper}>
        <div className={styles.dialogWrapper}>
          <div className={styles.header}>
            <h2 className={styles.title}>{title}</h2>
            <button className={styles.closeButton} onClick={onClose} type="button">
              <CloseIcon />
            </button>
            <div className={styles.tabs}>
              {Object.keys(steps).map(step => (
                <button
                  key={step}
                  onClick={() => setActiveStep(step)}
                  className={step === activeStep ? styles.activeTab : styles.tab}
                  type="button"
                >
                  {stepNames[step]}
                </button>
              ))}
            </div>
          </div>

          <div className={styles.content}>
            {activeStep === steps.configuration && (
              <ConfigurationStep
                isEnabled={isEnabled}
                setIsEnabled={setIsEnabled}
                timingKind={timingKind}
                timingLabel={timingLabel}
                daysBeforeCheckIn={daysBeforeCheckIn}
                setDaysBeforeCheckIn={setDaysBeforeCheckIn}
              />
            )}

            {activeStep === steps.text && (
              <TextMessageStep
                messageTemplate={messageTemplate}
                setMessageTemplate={setMessageTemplate}
              />
            )}

            {activeStep === steps.email && (
              <div className={styles.emailStepContent}>
                {contentId ? (
                  <EmailEditorPanel embedded hideFooter onClose={refreshEmailData} />
                ) : (
                  <p className={styles.emailUnavailable}>
                    Email content for this hotel is still being created. Close this dialog,
                    wait a moment for the page to finish loading, then open Edit again.
                  </p>
                )}
              </div>
            )}

            {activeStep === steps.landing && (
              <LandingContentStep
                landing={landing}
                setLanding={setLanding}
                hotelName={resolvedHotelName}
                firstName={firstName}
              />
            )}

            {activeStep === steps.incentives && (
              <IncentiveStep incentive={incentive} setIncentive={setIncentive} />
            )}
          </div>

          <div className={styles.footer}>
            <SecondaryRegularButton onClick={onClose} disabled={isSaving}>
              Cancel
            </SecondaryRegularButton>
            <PrimaryRegularButton onClick={handleSave} disabled={isSaving}>
              {isSaving ? 'Saving…' : 'Save draft'}
            </PrimaryRegularButton>
          </div>
        </div>

        {isEditingEmail && (
          <div className={`${styles.previewHolder} ${styles.previewHolderEmail}`}>
            <EmailPreview
              isEditable={false}
              holderClassName={styles.emailPreviewHolder}
              testHolderClassName={styles.emailTestHolder}
              skipInitialSectionReset
              hideEditContentButton
            >
              <TestEmail />
            </EmailPreview>
          </div>
        )}
      </div>
    </Dialog>
  )
}

EditStepDialogInner.propTypes = {
  title: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  isSaving: PropTypes.bool,
  contentId: PropTypes.number,
  hotelName: PropTypes.string,
  firstName: PropTypes.string,
  initialData: PropTypes.shape({
    isEnabled: PropTypes.bool,
    daysBeforeCheckIn: PropTypes.number,
    messageTemplate: PropTypes.string,
    landing: PropTypes.object,
    incentive: PropTypes.object
  }),
  timingKind: PropTypes.oneOf(['event', 'daysBeforeCheckIn']).isRequired,
  timingLabel: PropTypes.string.isRequired
}

function EditStepDialog({ contentId, contentCategory, ...props }) {
  return (
    <EmailEditorProvider contentId={contentId} contentCategory={contentCategory}>
      <EditStepDialogInner contentId={contentId} {...props} />
    </EmailEditorProvider>
  )
}

EditStepDialog.propTypes = {
  contentId: PropTypes.number,
  contentCategory: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  isSaving: PropTypes.bool,
  hotelName: PropTypes.string,
  firstName: PropTypes.string,
  initialData: PropTypes.shape({
    isEnabled: PropTypes.bool,
    daysBeforeCheckIn: PropTypes.number,
    messageTemplate: PropTypes.string,
    landing: PropTypes.object,
    incentive: PropTypes.object
  }),
  timingKind: PropTypes.oneOf(['event', 'daysBeforeCheckIn']).isRequired,
  timingLabel: PropTypes.string.isRequired
}

export default EditStepDialog
